/**
 * useAvailability - Availability Data Hook
 *
 * Fetches bookable slots for one service on one Nassau calendar day and
 * caches them in memory for 5 minutes.
 *
 * Each fetch is owned by the effect that started it: cleanup aborts the
 * request and ignores its result. This is safe under React 18 StrictMode,
 * which mounts, unmounts and remounts effects in development (the old
 * isMountedRef pattern stayed false after that and spun forever).
 *
 * @example
 * const { slots, loading, error, refetch } = useAvailability('individual-talk-therapy', '2026-10-05');
 * // slots: [{ startISO: '2026-10-05T13:30:00Z', endISO: '2026-10-05T14:20:00Z' }, ...]
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getAvailability } from '../services/api/availabilityService';
import { getErrorMessage, isCanceledError } from '../services/errors/errorHandler';

export const AVAILABILITY_CACHE_TTL_MS = 5 * 60 * 1000;

// { "<slug>|<YYYY-MM-DD>": { slots, timestamp } }
const cache = new Map();

const cacheKeyFor = (slug, date) => `${slug}|${date}`;

const getFromCache = (key) => {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp >= AVAILABILITY_CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.slots;
};

/**
 * Clear cached availability.
 * @param {string} [slug] - Only this service (all days). Omit to clear everything.
 * @param {string} [date] - Only this day of `slug`.
 */
export const clearAvailabilityCache = (slug, date) => {
  if (!slug) {
    cache.clear();
    return;
  }
  if (date) {
    cache.delete(cacheKeyFor(slug, date));
    return;
  }
  Array.from(cache.keys())
    .filter((key) => key.startsWith(`${slug}|`))
    .forEach((key) => cache.delete(key));
};

/** Clear every cached availability entry. */
export const clearAllAvailabilityCache = () => clearAvailabilityCache();

const IDLE = { slots: [], loading: false, error: null, errorCode: null };

/**
 * @param {string|{slug: string}|null} service - Service slug or service object
 * @param {string|null} date - 'YYYY-MM-DD' Nassau calendar day
 * @param {object} [options]
 * @param {boolean} [options.autoFetch=true] - Fetch when service/date change
 * @param {boolean} [options.useCache=true] - Use the 5-minute cache
 * @returns {{slots: Array<{startISO: string, endISO: string}>, loading: boolean, error: string|null, errorCode: string|null, refetch: Function, clearCache: Function}}
 */
const useAvailability = (service, date, options = {}) => {
  const { autoFetch = true, useCache = true } = options;
  const slug = typeof service === 'string' ? service : service?.slug || null;

  const [state, setState] = useState(IDLE);
  const [reloadCount, setReloadCount] = useState(0);
  const skipCacheRef = useRef(false);

  useEffect(() => {
    if (!autoFetch) return undefined;

    if (!slug || !date) {
      setState(IDLE);
      return undefined;
    }

    const key = cacheKeyFor(slug, date);
    const skipCache = skipCacheRef.current;
    skipCacheRef.current = false;

    if (useCache && !skipCache) {
      const cached = getFromCache(key);
      if (cached) {
        setState({ slots: cached, loading: false, error: null, errorCode: null });
        return undefined;
      }
    }

    const controller = new AbortController();
    let ignore = false;

    setState({ slots: [], loading: true, error: null, errorCode: null });

    getAvailability(date, slug, { signal: controller.signal }).then(
      (slots) => {
        if (ignore) return;
        if (useCache) {
          cache.set(key, { slots, timestamp: Date.now() });
        }
        setState({ slots, loading: false, error: null, errorCode: null });
      },
      (err) => {
        if (ignore || isCanceledError(err)) return;
        setState({
          slots: [],
          loading: false,
          error: getErrorMessage(err),
          errorCode: err?.code || null,
        });
      }
    );

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [slug, date, autoFetch, useCache, reloadCount]);

  /** Fetch again, bypassing the cache (e.g. after a 409 slot_unavailable). */
  const refetch = useCallback(() => {
    skipCacheRef.current = true;
    setReloadCount((n) => n + 1);
  }, []);

  /** Clear the cache entry for the current service/day. */
  const clearCache = useCallback(() => {
    if (slug && date) {
      clearAvailabilityCache(slug, date);
    }
  }, [slug, date]);

  return {
    slots: state.slots,
    loading: state.loading,
    error: state.error,
    errorCode: state.errorCode,
    refetch,
    clearCache,
  };
};

export default useAvailability;
